//changed due to package upgrade
import AppCard from '@projectK/components/AppCard';
import React, { useEffect, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Button,
  Grid,
  Stack,
  TextField,
} from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import PropTypes from 'prop-types';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import jwtAxios from '@projectK/services/auth/JWT';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { FilterModal } from '@projectK/ReUseFunctions';
import { isParent, isStudent } from '@projectK/context/RolesProvider';

const WaiversLogFilter = ({
  signedDateFilter,
  setProductFilter,
  setUserFilter,
  setSignedDateFilter,
  setPage,
  verbiageSettings,
  isMobile,
  openFilterModal,
  handleFilterModalClose,
}) => {
  const companyTheme = CompanyTheme();
  const { user } = useAuthUser();
  const [isExpandFilter, setIsExpandFilter] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTermProduct, setSearchTermProduct] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(true);
  const [productsOptions, setProductsOptions] = useState([]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'productId') {
      setProductFilter(value ? value.id : '');
      setPage(0);
    }
    if (filterType === 'userId') {
      setUserFilter(value ? value.id : '');
      setPage(0);
    }
    if (filterType === 'signedOn') {
      if (value === '') {
        setSignedDateFilter('');
      } else {
        setSignedDateFilter(value);
      }
      setPage(0);
    }
  };
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const toggleFilter = () => {
    setIsExpandFilter(!isExpandFilter);
  };

  const clearFilter = () => {
    setSignedDateFilter('');
    setUserFilter('');
    setProductFilter('');
    setSelectedProduct(null);
    setSelectedUser(null);
    setPage(0);
  };

  const filteredDate = new Date(
    `${signedDateFilter?.substring(0, 10)}T12:01:00`,
  );
  useEffect(() => {
    if (!isStudent(user)) {
      fetchUsersData();
    }
  }, [searchTerm]);

  const fetchUsersData = async () => {
    setLoading(true);
    try {
      let url = `/users`;
      if (isParent(user)) {
        url = `/user-members/${user.id}`;
      }
      const response = await jwtAxios.get(url, {
        params: {
          perPage: 'all',
          search: searchTerm,
        },
      });
      const { data } = response;
      setUsers(data?.list);
      setLoading(false);
    } catch (error) {
      setUsers([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductData();
  }, [searchTermProduct]);

  const fetchProductData = async () => {
    setProductLoading(true);
    try {
      const response = await jwtAxios.get(`/products`, {
        params: {
          perPage: 'all',
          search: searchTermProduct,
        },
      });
      const { data } = response;
      setProductsOptions(data?.list);

      setProductLoading(false);
    } catch (error) {
      setProductsOptions([]);
      setProductLoading(false);
    }
  };
  const modalContent = (
    <AppGridContainer
      spacing={5}
      onClick={stopPropagation}
      sx={{ marginTop: '2px' }}
    >
      <Grid size={{ xs: 12, md: 4, sm: 6 }}>
        <Autocomplete
          options={(!productLoading ? productsOptions : []).sort((a, b) =>
            a.product_name.localeCompare(b.product_name, undefined, {
              numeric: true,
              sensitivity: 'base',
            }),
          )}
          getOptionLabel={(option) =>
            option.badge
              ? `${option.product_name} - ${option.badge}`
              : option.product_name
          }
          value={selectedProduct}
          onChange={(_, value) => {
            handleFilterChange('productId', value || {});
            setSelectedProduct(value);
          }}
          inputValue={searchTermProduct}
          onInputChange={(_, value) => setSearchTermProduct(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${verbiageSettings?.product_type || 'Products'}`}
              variant='outlined'
            />
          )}
        />
      </Grid>
      {!isStudent(user) && (
        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <Autocomplete
            options={(!loading ? users : []).sort((a, b) =>
              (a.first_name + ' ' + a.last_name).localeCompare(
                b.first_name + ' ' + b.last_name,
                undefined,
                { numeric: true, sensitivity: 'base' },
              ),
            )}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name} - ${option.email}`
            }
            value={selectedUser}
            onChange={(_, value) => {
              handleFilterChange('userId', value || {});
              setSelectedUser(value);
            }}
            inputValue={searchTerm}
            onInputChange={(_, value) => setSearchTerm(value)}
            renderInput={(params) => (
              <TextField {...params} label='Participants' variant='outlined' />
            )}
          />
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 4, sm: 6 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={1}>
            <DatePicker
              label='Signed On'
              sx={{
                width: '100%',
                backgroundColor: 'background.paper',
                color: 'text.primary',
              }}
              value={signedDateFilter ? filteredDate : null}
              onChange={(value) => {
                const formattedDate =
                  value && !isNaN(value.getTime())
                    ? dayjs(value).startOf('day').format('YYYY-MM-DD')
                    : '';
                handleFilterChange('signedOn', formattedDate);
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </Stack>
        </LocalizationProvider>
      </Grid>

      {!isMobile && (
        <Grid size={{ xs: 12, md: 4, sm: 6 }}>
          <Button
            variant='outlined'
            sx={{
              borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: companyTheme ? companyTheme.primary.main : '#2196f3',
            }}
            onClick={clearFilter}
          >
            Clear Filter
          </Button>
        </Grid>
      )}
    </AppGridContainer>
  );

  return isMobile ? (
    <FilterModal
      modalContent={modalContent}
      isOpen={openFilterModal}
      onClose={handleFilterModalClose}
      clearFilter={clearFilter}
      companyTheme={companyTheme}
      title={`Filter ${verbiageSettings?.product_type || 'Product'} ${
        verbiageSettings?.waiver_name_plural || 'Waivers'
      } Log`}
    />
  ) : (
    <AppCard
      className='right-main-content'
      title={`Filter ${verbiageSettings?.product_type || 'Product'} ${
        verbiageSettings?.waiver_name_plural || 'Waivers'
      } Log`}
      style={{ position: 'relative' }}
      onClick={toggleFilter}
    >
      {modalContent}
    </AppCard>
  );
};

WaiversLogFilter.propTypes = {
  productFilter: PropTypes.string.isRequired,
  setProductFilter: PropTypes.func.isRequired,
  isFilterOpen: PropTypes.bool.isRequired,
  setIsFilterOpen: PropTypes.func.isRequired,
  signedDateFilter: PropTypes.string,
  setUserFilter: PropTypes.func,
  setSignedDateFilter: PropTypes.func,
  setPage: PropTypes.func,
  verbiageSettings: PropTypes.object,
  isMobile: PropTypes.bool,
  openFilterModal: PropTypes.bool,
  handleFilterModalClose: PropTypes.func,
};

export default WaiversLogFilter;
